import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { tenantApi } from '../api/endpoints/tenant';
import type { UserFieldDefLocal } from '../api/types/TenantLocal';

interface UseTenantUserFieldsOptions {
    /**
     * Si `true`, devuelve SOLO los campos con `enabled === true` (lo que necesitan los forms de
     * usuario). Si `false`/omitido, devuelve TODOS (lo que necesita la pantalla de catálogo CRUD).
     *
     * DECISIÓN: el filtro vive en el `select` de TanStack Query (no en los consumidores) por dos
     * motivos: (1) la query cachea SIEMPRE la lista completa bajo el mismo `queryKey` — un único
     * fetch sirve a ambos consumidores; (2) `select` solo transforma la vista derivada sin re-pedir.
     * Así el catálogo (todos) y los forms (enabled) comparten caché sin duplicar requests.
     */
    enabledOnly?: boolean;
}

/**
 * Catálogo de campos personalizados del tenant (`UserFieldDef[]`).
 *
 * `queryKey: ['tenant', tid, 'user-fields']` — espeja la convención del repo (ver `useRoleNameById`,
 * `ScopedUserForm`). La query NO dispara sin `tenantId` (`enabled: !!tenantId`).
 *
 * El orden de presentación (alfabético por `label`, default del proyecto) lo resuelve el consumidor
 * de UI, no este hook — aquí se devuelve la lista tal cual la sirve el backend.
 */
export function useTenantUserFields(
    tenantId: string | undefined,
    { enabledOnly = false }: UseTenantUserFieldsOptions = {},
): UseQueryResult<UserFieldDefLocal[]> {
    return useQuery({
        queryKey: ['tenant', tenantId, 'user-fields'],
        queryFn: () => tenantApi.listUserFields(tenantId ?? ''),
        enabled: !!tenantId,
        select: enabledOnly ? (fields) => fields.filter((f) => f.enabled) : undefined,
    });
}
